import { useToolStore } from '../store/tools';
import { Tools } from '../store/tools/types';
import { useRectangleStore } from '../store/tools/rectangles';
import { useRulerStore } from '../store/tools/rulers';
import { usePolygonStore } from '../store/tools/polygons';
import { useAngleStore } from '../store/tools/angles';
import { useArrowStore } from '../store/tools/arrows';
import { useCircleStore } from '../store/tools/circles';
import { useFreehandStore } from '../store/tools/freehands';
import { useViewStore } from '../store/views';
import { Action, NOOP } from '../constants';
import { useKeyboardShortcutsStore } from '../store/keyboard-shortcuts';
import { useCurrentImage } from './useCurrentImage';
import { useSliceConfig } from './useSliceConfig';
import { useCineFrame } from './useCineFrame';
import { useDatasetStore } from '../store/datasets';
import { usePaintToolStore } from '../store/tools/paint';
import { PaintMode } from '../core/tools/paint';
import { computeEffectiveView } from '../core/views/effectiveView';
import { usePycadUiStore } from '../store/pycad-ui';
import { isFeatureCompiled } from '@/src/features/featureFlags';

const applyLabelOffset = (offset: number) => () => {
  if (!__FEATURE_ANNOTATIONS__) return;

  const toolToStore = {
    [Tools.Rectangle]: useRectangleStore(),
    [Tools.Ruler]: useRulerStore(),
    [Tools.Polygon]: usePolygonStore(),
    [Tools.Angle]: useAngleStore(),
    [Tools.Arrow]: useArrowStore(),
    [Tools.Circle]: useCircleStore(),
    [Tools.Freehand]: useFreehandStore(),
  };
  const toolStore = useToolStore();

  // @ts-ignore - toolToStore may not have keys of all tools
  const activeToolStore = toolToStore[toolStore.currentTool];
  if (!activeToolStore) return;

  const labels = Object.entries(activeToolStore.labels);
  const activeLabelIndex = labels.findIndex(
    ([name]) => name === activeToolStore.activeLabel
  );

  const [nextLabel] = labels.at((activeLabelIndex + offset) % labels.length)!;
  activeToolStore.setActiveLabel(nextLabel);
};

const setTool = (tool: Tools) => () => {
  useToolStore().setCurrentTool(tool);
};

const startPaintInMode = (mode: PaintMode) => () => {
  useToolStore().setCurrentTool(Tools.Paint);
  usePaintToolStore().setMode(mode);
};

const showKeyboardShortcuts = () => {
  const keyboardStore = useKeyboardShortcutsStore();
  keyboardStore.settingsOpen = !keyboardStore.settingsOpen;
};

const changeSlice = (offset: number) => () => {
  const { currentImageID } = useCurrentImage();
  const viewStore = useViewStore();
  const { activeView } = viewStore;
  if (!activeView) return;

  const view = viewStore.getView(activeView);
  if (!view) return;

  const effective = computeEffectiveView(view, currentImageID.value);
  if (effective.kind === 'cine') {
    const { frame, setFrame } = useCineFrame(activeView, currentImageID);
    setFrame(frame.value + offset);
    return;
  }

  const { slice: currentSlice } = useSliceConfig(activeView, currentImageID);
  currentSlice.value += offset;
};

const clearScene = () => () => {
  const datasetStore = useDatasetStore();
  datasetStore.resetViewerForNewScan();
};

const deleteCurrentImage = () => () => {
  const { currentImageID } = useCurrentImage();
  if (currentImageID.value) {
    const datasetStore = useDatasetStore();
    datasetStore.remove(currentImageID.value);
  }
};

const changeBrushSize = (delta: number) => () => {
  const paintStore = usePaintToolStore();
  const newSize = Math.max(1, paintStore.brushSize + delta);
  paintStore.setBrushSize(newSize);
};

export const ACTION_TO_FUNC = {
  windowLevel: setTool(Tools.WindowLevel),
  pan: setTool(Tools.Pan),
  zoom: setTool(Tools.Zoom),
  ruler: __FEATURE_ANNOTATIONS__ ? setTool(Tools.Ruler) : NOOP,
  paint: startPaintInMode(PaintMode.CirclePaint),
  paintEraser: startPaintInMode(PaintMode.Erase),
  brushSizeModifier: NOOP,
  decreaseBrushSize: changeBrushSize(-1),
  increaseBrushSize: changeBrushSize(1),
  rectangle: __FEATURE_ANNOTATIONS__ ? setTool(Tools.Rectangle) : NOOP,
  crosshairs: setTool(Tools.Crosshairs),
  temporaryCrosshairs: NOOP,
  crop: __FEATURE_CROPPING__ ? setTool(Tools.Crop) : NOOP,
  polygon: __FEATURE_ANNOTATIONS__ ? setTool(Tools.Polygon) : NOOP,
  angle: __FEATURE_ANNOTATIONS__ ? setTool(Tools.Angle) : NOOP,
  arrow: __FEATURE_ANNOTATIONS__ ? setTool(Tools.Arrow) : NOOP,
  circle: __FEATURE_ANNOTATIONS__ ? setTool(Tools.Circle) : NOOP,
  freehand: __FEATURE_ANNOTATIONS__ ? setTool(Tools.Freehand) : NOOP,
  select: setTool(Tools.Select),

  nextSlice: changeSlice(-1),
  previousSlice: changeSlice(1),
  grabSlice: NOOP,

  decrementLabel: __FEATURE_ANNOTATIONS__ ? applyLabelOffset(-1) : NOOP,
  incrementLabel: __FEATURE_ANNOTATIONS__ ? applyLabelOffset(1) : NOOP,

  deleteCurrentImage: deleteCurrentImage(),
  clearScene: clearScene(),

  mergeNewPolygon: NOOP,

  panoramic: isFeatureCompiled('implantPlanning')
    ? () => {
        usePycadUiStore().toggleCurvedMPRWorkspace();
      }
    : NOOP,

  showKeyboardShortcuts,
} as const satisfies Record<Action, () => void>;
